import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Ban, Banknote, Receipt } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { Seccion } from '@/shared/ui/Seccion';
import { Barras, enDinero } from '@/shared/ui/graficos';
import { Cifra } from '@/modules/inicio/piezas';
import { BotonesDeExportacion } from '@/modules/reportes/BotonesDeExportacion';

interface FilaDeCooperativa {
  cooperativa: string;
  clave: string;
  cobros: number;
  anulados: number;
  capital: number;
  interes: number;
  total: number;
  reversado: number;
  periodo: string;
}

interface Cobro {
  id: number;
  cooperativa: string;
  fecha: string;
  comprobante: string;
  cliente: string;
  identificacion: string;
  facturas: number;
  capital: number;
  interes: number;
  total: number;
  anulado: boolean;
}

interface Panel {
  cobros: number;
  anulados: number;
  total: number;
  cooperativas: FilaDeCooperativa[];
  ultimos: Cobro[];
}

function enIso(fecha: Date): string {
  const dosCifras = (n: number) => String(n).padStart(2, '0');
  return `${fecha.getFullYear()}-${dosCifras(fecha.getMonth() + 1)}-${dosCifras(fecha.getDate())}`;
}

function enClave(clave: string): string {
  return clave.replace(/_/g, ' ').toLocaleUpperCase('es');
}

export function PanelDeRecaudacion() {
  const hoy = new Date();
  const [desde, setDesde] = useState(enIso(new Date(hoy.getFullYear(), hoy.getMonth(), 1)));
  const [hasta, setHasta] = useState(enIso(hoy));
  const [cual, setCual] = useState('');

  const panel = useQuery({
    queryKey: ['api-publica', 'recaudacion', desde, hasta],
    queryFn: () => api.get<Panel>('/api-publica/recaudacion/panel', { query: { desde, hasta } }),
  });

  const datos = panel.data;
  const todas = datos?.cooperativas ?? [];
  const cooperativas = cual ? todas.filter((c) => c.clave === cual) : todas;
  const elegida = cual ? cooperativas[0] : undefined;

  const cobros = (datos?.ultimos ?? []).filter(
    (c) => !elegida || c.cooperativa === elegida.cooperativa,
  );
  const total = cooperativas.reduce((suma, c) => suma + c.total, 0);
  const cuantos = cooperativas.reduce((suma, c) => suma + c.cobros, 0);
  const reversado = cooperativas.reduce((suma, c) => suma + c.reversado, 0);
  const anulados = cooperativas.reduce((suma, c) => suma + c.anulados, 0);
  const activas = cooperativas.filter((c) => c.cobros > 0).length;

  const exportar = cual ? { desde, hasta, cooperativa: cual } : { desde, hasta };
  const comoSeLlama = cual ? `-${cual}` : '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-armazon-200 bg-white p-4">
        <span className="text-[11px] font-bold uppercase tracking-wide text-armazon-600">
          Período
        </span>
        <input
          type="date"
          value={desde}
          max={hasta}
          onChange={(e) => setDesde(e.target.value)}
          aria-label="Desde"
          className="h-9 rounded-lg border border-armazon-200 px-2 text-xs text-armazon-700"
        />
        <span className="text-[11px] uppercase text-armazon-400">a</span>
        <input
          type="date"
          value={hasta}
          min={desde}
          onChange={(e) => setHasta(e.target.value)}
          aria-label="Hasta"
          className="h-9 rounded-lg border border-armazon-200 px-2 text-xs text-armazon-700"
        />

        <select
          value={cual}
          onChange={(e) => setCual(e.target.value)}
          aria-label="Qué cooperativa"
          className="h-9 rounded-lg border border-armazon-200 bg-white px-2 text-[11px] font-semibold uppercase tracking-wide text-armazon-700 outline-none focus:border-marca-400"
        >
          <option value="">TODAS LAS COOPERATIVAS</option>
          {todas.map((c) => (
            <option key={c.clave} value={c.clave}>
              {c.cooperativa.toLocaleUpperCase('es')}
            </option>
          ))}
        </select>

        <span className="ml-auto flex flex-wrap items-center gap-3">
          <BotonesDeExportacion
            rotulo={cual ? 'Su liquidación' : 'Liquidación'}
            ruta="/api-publica/recaudacion"
            nombre={`liquidacion${comoSeLlama || '-por-cooperativa'}`}
            parametros={exportar}
          />
          {anulados > 0 && (
            <BotonesDeExportacion
              rotulo="Reversas"
              ruta="/api-publica/recaudacion/reversas"
              nombre={`reversas${comoSeLlama || '-de-cooperativas'}`}
              parametros={exportar}
            />
          )}
        </span>
      </div>

      {panel.isLoading || !datos ? (
        <Cargando titulo="Cargando" detalle="sumando lo que cobró cada cooperativa" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
            <Cifra
              icono={Banknote}
              rotulo="A liquidar"
              valor={enDinero(total)}
              pie="COBRADO MENOS REVERSADO"
              tono="esmeralda"
            />
            <Cifra
              icono={Receipt}
              rotulo="Cobros"
              valor={cuantos.toLocaleString('es-EC')}
              pie={
                elegida
                  ? enClave(elegida.clave)
                  : `${activas} DE ${cooperativas.length} COOPERATIVAS ACTIVAS`
              }
              tono="azul"
            />
            <Cifra
              icono={Ban}
              rotulo="Reversado"
              valor={enDinero(reversado)}
              pie={`${anulados.toLocaleString('es-EC')} COBRO${anulados === 1 ? '' : 'S'} DESHECHO${anulados === 1 ? '' : 'S'}, FUERA DEL TOTAL`}
              tono={anulados > 0 ? 'ambar' : 'pizarra'}
            />
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <Seccion
              titulo={elegida ? 'Lo que se le cobra' : 'Cuánto se le cobra a cada una'}
              ayuda="SIN LO REVERSADO"
              ancho="xl:col-span-2"
            >
              {cooperativas.length === 0 ? (
                <p className="p-8 text-center text-xs uppercase tracking-wide text-armazon-400">
                  Ninguna cooperativa está autorizada a recaudar todavía
                </p>
              ) : (
                <Barras
                  datos={cooperativas}
                  claveEtiqueta="cooperativa"
                  claveValor="total"
                  nombre="Recaudado"
                />
              )}
            </Seccion>

            <Seccion titulo="Detalle" ayuda="CON LA CLAVE POR LA QUE SE IDENTIFICAN">
              {cooperativas.length === 0 ? (
                <p className="p-8 text-center text-xs uppercase tracking-wide text-armazon-400">
                  Sin cooperativas
                </p>
              ) : (
                <ul className="divide-y divide-armazon-100">
                  {cooperativas.map((c) => (
                    <li key={c.clave} className="px-2 py-2.5">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 truncate text-xs font-medium uppercase text-armazon-800">
                          {c.cooperativa}
                        </span>
                        <span className="shrink-0 text-xs font-semibold tabular-nums text-marca-700">
                          {enDinero(c.total)}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] uppercase tracking-wide text-armazon-400">
                        <span className="rounded bg-armazon-100 px-1.5 py-0.5 font-semibold text-armazon-500">
                          {enClave(c.clave)}
                        </span>
                        <span>
                          {c.cobros.toLocaleString('es-EC')} COBROS
                          {c.anulados > 0 && (
                            <span className="text-amber-700">
                              {' '}
                              · {c.anulados} REVERSADO{c.anulados === 1 ? '' : 'S'} POR{' '}
                              {enDinero(c.reversado)}
                            </span>
                          )}
                        </span>
                        {c.periodo && <span className="truncate">{c.periodo}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Seccion>
          </div>

          <Seccion
            titulo="Cobro a cobro"
            ayuda="PARA CUADRAR CONTRA LA LIQUIDACIÓN QUE MANDA LA COOPERATIVA"
          >
            {cobros.length === 0 ? (
              <p className="p-8 text-center text-xs uppercase tracking-wide text-armazon-400">
                {elegida
                  ? 'Esa cooperativa no cobró nada en el período'
                  : 'Ninguna cooperativa cobró nada en el período'}
              </p>
            ) : (
              <div className="max-h-[28rem] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-armazon-50/95">
                    <tr>
                      {['Fecha', 'Cooperativa', 'Comprobante', 'Contribuyente', 'Fact.', 'Total'].map(
                        (c, i) => (
                          <th
                            key={c}
                            scope="col"
                            className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-armazon-500 ${
                              i >= 4 ? 'text-right' : 'text-left'
                            }`}
                          >
                            {c}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-armazon-100">
                    {cobros.map((c) => (
                      <tr
                        key={c.id}
                        className={`transition-colors hover:bg-armazon-50/60 ${
                          c.anulado ? 'text-armazon-400 line-through' : ''
                        }`}
                      >
                        <td className="whitespace-nowrap px-3 py-2 text-[11px] tabular-nums text-armazon-500">
                          {c.fecha}
                        </td>
                        <td className="px-3 py-2 text-xs uppercase text-armazon-700">
                          {c.cooperativa}
                        </td>
                        <td className="px-3 py-2 text-[11px] tabular-nums text-armazon-500">
                          {c.comprobante}
                        </td>
                        <td className="px-3 py-2 text-xs uppercase text-armazon-700">
                          {c.cliente}
                          <span className="block text-[11px] tabular-nums text-armazon-400">
                            {c.identificacion}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right text-xs tabular-nums text-armazon-500">
                          {c.facturas}
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-semibold tabular-nums">
                          {enDinero(c.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Seccion>
        </>
      )}
    </div>
  );
}
